import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import { Trash2, Plus, Users, Upload } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/segments")({
  component: SegmentsPage,
});

type SegmentKind = "dynamic" | "static";
type Lifecycle = "lead" | "trial" | "active" | "churned" | "vip";

type Segment = {
  id: string;
  name: string;
  description: string | null;
  kind: SegmentKind;
  rules: { all?: Rule[] } | null;
  lifecycle_stage: Lifecycle | null;
  color: string;
  created_at: string;
};

type Rule = { field: "tag" | "lifecycle_stage" | "min_orders" | "min_spend_cents"; op: "eq" | "gte" | "has"; value: string };

const STAGES: Lifecycle[] = ["lead", "trial", "active", "churned", "vip"];
const COLORS = ["slate", "blue", "green", "amber", "rose", "violet"];

function SegmentsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Segment | null>(null);
  const [open, setOpen] = useState(false);
  const [memberView, setMemberView] = useState<Segment | null>(null);
  const [q, setQ] = useState("");
  const [kindF, setKindF] = useState<"all" | SegmentKind>("all");


  const segments = useQuery({
    queryKey: ["segments"],
    queryFn: async () => {
      const { data, error } = await supabase.from("customer_segments").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Segment[];
    },
  });

  const traits = useQuery({
    queryKey: ["customer_traits"],
    queryFn: async () => {
      const { data, error } = await supabase.from("customer_traits").select("*").limit(2000);
      if (error) throw error;
      return data ?? [];
    },
  });

  const orderAgg = useQuery({
    queryKey: ["segment_order_agg"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("user_id, total_cents, status")
        .in("status", ["paid", "fulfilled"])
        .limit(5000);
      if (error) throw error;
      const map = new Map<string, { orders: number; spend: number }>();
      for (const o of data ?? []) {
        if (!o.user_id) continue;
        const cur = map.get(o.user_id) ?? { orders: 0, spend: 0 };
        cur.orders += 1;
        cur.spend += o.total_cents ?? 0;
        map.set(o.user_id, cur);
      }
      return map;
    },
  });

  const staticMembers = useQuery({
    queryKey: ["segment_members"],
    queryFn: async () => {
      const { data, error } = await supabase.from("segment_members").select("segment_id, user_id");
      if (error) throw error;
      const map = new Map<string, string[]>();
      for (const m of data ?? []) {
        const arr = map.get(m.segment_id) ?? [];
        arr.push(m.user_id);
        map.set(m.segment_id, arr);
      }
      return map;
    },
  });

  const evaluate = (seg: Segment): Set<string> => {
    if (seg.kind === "static") return new Set(staticMembers.data?.get(seg.id) ?? []);
    const rules = seg.rules?.all ?? [];
    const userIds = new Set<string>();
    for (const t of traits.data ?? []) userIds.add(t.user_id);
    for (const uid of orderAgg.data?.keys() ?? []) userIds.add(uid);

    const matches = new Set<string>();
    for (const uid of userIds) {
      const t = (traits.data ?? []).find(x => x.user_id === uid);
      const agg = orderAgg.data?.get(uid) ?? { orders: 0, spend: 0 };
      const ok = rules.every(r => {
        if (r.field === "tag") return (t?.tags ?? []).includes(r.value);
        if (r.field === "lifecycle_stage") return t?.lifecycle_stage === r.value;
        if (r.field === "min_orders") return agg.orders >= Number(r.value || 0);
        if (r.field === "min_spend_cents") return agg.spend >= Number(r.value || 0);
        return false;
      });
      if (ok && rules.length > 0) matches.add(uid);
    }
    return matches;
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this segment?")) return;
    const { error } = await supabase.from("customer_segments").delete().eq("id", id);
    if (error) toast.error(error.message);
    else { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["segments"] }); }
  };

  // Lifecycle distribution
  const lifecycleStats = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of traits.data ?? []) {
      const s = t.lifecycle_stage ?? "unassigned";
      counts[s] = (counts[s] ?? 0) + 1;
    }
    return counts;
  }, [traits.data]);

  return (
    <div className="space-y-4">
      <AdminPageHeader
        title="Customer segments"
        description="Group customers by behavior, plan, or custom traits."
        search={q}
        onSearchChange={setQ}
        searchPlaceholder="Search segments…"
        count={segments.data?.length}
        countLabel="segments"
        onNew={() => { setEditing(null); setOpen(true); }}
        newLabel="New segment"
      />

      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {["lead", "trial", "active", "churned", "vip", "unassigned"].map(s => (
          <Card key={s}>
            <CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{s}</div>
              <div className="mt-0.5 text-xl font-semibold">{lifecycleStats[s] ?? 0}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {(["all", "dynamic", "static"] as const).map((k) => (
          <button key={k} onClick={() => setKindF(k)} className={`rounded-full border px-2.5 py-1 text-xs capitalize ${kindF === k ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary/30 text-muted-foreground hover:bg-secondary"}`}>{k}</button>
        ))}
      </div>

      <Tabs defaultValue="segments">
        <PillTabsList search>
          <PillTabsTrigger value="segments">Segments</PillTabsTrigger>
          <PillTabsTrigger value="traits">Customer traits</PillTabsTrigger>
        </PillTabsList>

        <TabsContent value="segments" className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {segments.isLoading && <div className="text-muted-foreground">Loading…</div>}
            {!segments.isLoading && (segments.data ?? []).filter(sg => (kindF === "all" || sg.kind === kindF) && (!q.trim() || sg.name.toLowerCase().includes(q.toLowerCase()))).length === 0 && (
              <div className="text-muted-foreground">No segments match.</div>
            )}
            {(segments.data ?? []).filter(sg => (kindF === "all" || sg.kind === kindF) && (!q.trim() || sg.name.toLowerCase().includes(q.toLowerCase()))).map(seg => {

              const members = evaluate(seg);
              return (
                <Card key={seg.id}>
                  <CardHeader className="flex flex-row items-start justify-between space-y-0">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: `var(--${seg.color}, hsl(var(--primary)))` }} />
                        {seg.name}
                      </CardTitle>
                      <div className="mt-1 flex gap-2">
                        <Badge variant="outline">{seg.kind}</Badge>
                        {seg.lifecycle_stage && <Badge variant="secondary">{seg.lifecycle_stage}</Badge>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => setMemberView(seg)} title="View members">
                        <Users className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => { setEditing(seg); setOpen(true); }}>
                        Edit
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(seg.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-2">{seg.description || "—"}</p>
                    <div className="mt-3 text-2xl font-semibold">{members.size.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">members</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="traits">
          <TraitsTab traits={traits.data ?? []} onChange={() => qc.invalidateQueries({ queryKey: ["customer_traits"] })} />
        </TabsContent>
      </Tabs>

      <SegmentDialog
        open={open}
        onOpenChange={setOpen}
        segment={editing}
        onSaved={() => { qc.invalidateQueries({ queryKey: ["segments"] }); qc.invalidateQueries({ queryKey: ["segment_members"] }); }}
      />

      <Dialog open={!!memberView} onOpenChange={(o) => !o && setMemberView(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{memberView?.name} — members</DialogTitle>
          </DialogHeader>
          {memberView && <MembersList userIds={Array.from(evaluate(memberView))} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MembersList({ userIds }: { userIds: string[] }) {
  const { data } = useQuery({
    queryKey: ["segment_member_profiles", userIds.sort().join(",")],
    queryFn: async () => {
      if (!userIds.length) return [];
      const { data, error } = await supabase.from("profiles").select("id, display_name, avatar_url").in("id", userIds).limit(500);
      if (error) throw error;
      return data ?? [];
    },
  });
  if (!userIds.length) return <p className="text-muted-foreground text-sm">No members match.</p>;
  return (
    <div className="max-h-[400px] overflow-auto divide-y divide-border">
      {(data ?? []).map(p => (
        <div key={p.id} className="flex items-center gap-3 py-2">
          {p.avatar_url ? <img src={p.avatar_url} alt="" className="h-7 w-7 rounded-full" /> : <div className="h-7 w-7 rounded-full bg-secondary" />}
          <div className="text-sm">{p.display_name ?? p.id.slice(0, 8)}</div>
        </div>
      ))}
      {userIds.length > (data?.length ?? 0) && (
        <div className="py-2 text-xs text-muted-foreground">+ {userIds.length - (data?.length ?? 0)} more</div>
      )}
    </div>
  );
}

function SegmentDialog({
  open, onOpenChange, segment, onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  segment: Segment | null;
  onSaved: () => void;
}) {
  const [name, setName] = useState(segment?.name ?? "");
  const [description, setDescription] = useState(segment?.description ?? "");
  const [kind, setKind] = useState<SegmentKind>(segment?.kind ?? "dynamic");
  const [stage, setStage] = useState<Lifecycle | "">(segment?.lifecycle_stage ?? "");
  const [color, setColor] = useState(segment?.color ?? "slate");
  const [rules, setRules] = useState<Rule[]>(segment?.rules?.all ?? []);
  const [csv, setCsv] = useState("");

  // reset when opening different segment
  useMemo(() => {
    if (open) {
      setName(segment?.name ?? "");
      setDescription(segment?.description ?? "");
      setKind(segment?.kind ?? "dynamic");
      setStage(segment?.lifecycle_stage ?? "");
      setColor(segment?.color ?? "slate");
      setRules(segment?.rules?.all ?? []);
      setCsv("");
    }
  }, [open, segment]);

  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    const payload = {
      name: name.trim(),
      description: description || null,
      kind,
      rules: { all: rules } as any,
      lifecycle_stage: stage || null,
      color,
    };
    let segId = segment?.id;
    if (segment) {
      const { error } = await supabase.from("customer_segments").update(payload).eq("id", segment.id);
      if (error) { toast.error(error.message); return; }
    } else {
      const { data, error } = await supabase.from("customer_segments").insert(payload).select("id").single();
      if (error) { toast.error(error.message); return; }
      segId = data.id;
    }

    if (kind === "static" && csv.trim() && segId) {
      const ids = csv.split(/[\s,;\n]+/).map(s => s.trim()).filter(Boolean);
      if (ids.length) {
        const rows = ids.map(user_id => ({ segment_id: segId!, user_id }));
        const { error } = await supabase.from("segment_members").upsert(rows, { onConflict: "segment_id,user_id" });
        if (error) { toast.error(`Members import: ${error.message}`); return; }
      }
    }

    toast.success("Saved");
    onSaved();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{segment ? "Edit segment" : "New segment"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label>Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="High-value customers" />
          </div>
          <div className="grid gap-2">
            <Label>Description</Label>
            <Textarea value={description ?? ""} onChange={e => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="grid gap-2">
              <Label>Type</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as SegmentKind)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dynamic">Dynamic (rules)</SelectItem>
                  <SelectItem value="static">Static (list)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Lifecycle</Label>
              <Select value={stage || "none"} onValueChange={(v) => setStage(v === "none" ? "" : (v as Lifecycle))}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Color</Label>
              <Select value={color} onValueChange={setColor}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{COLORS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {kind === "dynamic" ? (
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label>Rules (all must match)</Label>
                <Button size="sm" variant="outline" onClick={() => setRules(r => [...r, { field: "tag", op: "has", value: "" }])}>
                  <Plus className="mr-1 h-3 w-3" /> Rule
                </Button>
              </div>
              {rules.length === 0 && <p className="text-xs text-muted-foreground">No rules yet — segment will be empty.</p>}
              {rules.map((r, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Select value={r.field} onValueChange={(v) => setRules(arr => arr.map((x, j) => j === i ? { ...x, field: v as Rule["field"] } : x))}>
                    <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tag">Tag</SelectItem>
                      <SelectItem value="lifecycle_stage">Lifecycle stage</SelectItem>
                      <SelectItem value="min_orders">Min orders</SelectItem>
                      <SelectItem value="min_spend_cents">Min spend (cents)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input value={r.value} onChange={e => setRules(arr => arr.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} placeholder="value" />
                  <Button size="icon" variant="ghost" onClick={() => setRules(arr => arr.filter((_, j) => j !== i))}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid gap-2">
              <Label className="flex items-center gap-2"><Upload className="h-4 w-4" /> Import user IDs (CSV, one per line or comma-separated)</Label>
              <Textarea value={csv} onChange={e => setCsv(e.target.value)} rows={5} placeholder="uuid-1, uuid-2&#10;uuid-3" />
              <p className="text-xs text-muted-foreground">Existing members are kept; this adds more.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TraitsTab({ traits, onChange }: { traits: any[]; onChange: () => void }) {
  const [userId, setUserId] = useState("");
  const [tags, setTags] = useState("");
  const [stage, setStage] = useState<Lifecycle | "">("");

  const save = async () => {
    if (!userId.trim()) { toast.error("User ID required"); return; }
    const tagArr = tags.split(",").map(t => t.trim()).filter(Boolean);
    const { error } = await supabase.from("customer_traits").upsert({
      user_id: userId.trim(),
      tags: tagArr,
      lifecycle_stage: stage || null,
    }, { onConflict: "user_id" });
    if (error) toast.error(error.message);
    else { toast.success("Saved"); setUserId(""); setTags(""); setStage(""); onChange(); }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_2fr]">
      <Card>
        <CardHeader><CardTitle className="text-base">Add / update traits</CardTitle></CardHeader>
        <CardContent className="grid gap-3">
          <div className="grid gap-2">
            <Label>User ID</Label>
            <Input value={userId} onChange={e => setUserId(e.target.value)} placeholder="uuid" />
          </div>
          <div className="grid gap-2">
            <Label>Tags (comma-separated)</Label>
            <Input value={tags} onChange={e => setTags(e.target.value)} placeholder="vip, beta-tester" />
          </div>
          <div className="grid gap-2">
            <Label>Lifecycle stage</Label>
            <Select value={stage || "none"} onValueChange={(v) => setStage(v === "none" ? "" : (v as Lifecycle))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={save}>Save</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Recent traits</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-xs">
              <tr>
                <th className="px-4 py-2 text-left">User</th>
                <th className="px-4 py-2 text-left">Stage</th>
                <th className="px-4 py-2 text-left">Tags</th>
              </tr>
            </thead>
            <tbody>
              {traits.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-muted-foreground">No traits yet.</td></tr>}
              {traits.slice(0, 50).map((t) => (
                <tr key={t.id} className="border-t border-border">
                  <td className="px-4 py-2 font-mono text-xs">{t.user_id.slice(0, 8)}…</td>
                  <td className="px-4 py-2">{t.lifecycle_stage ?? "—"}</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(t.tags ?? []).map((tag: string) => <Badge key={tag} variant="outline">{tag}</Badge>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
